import { AfterViewInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { WIZ_UPLOAD_ACCEPT_FORMAT } from '@wizeoo/akm-core-lib';
import { IWizItemRenderer, UploadParameter, WizButton, WizHgroup, WizImage, WizVgroup } from '@wizeoo/akm-component-lib';
import { Observable, of } from 'rxjs';
import { ViewData } from '../../../../model/view.data';
import { ConnectVO } from '../../../../vo/core/connect.vo';
import { GridsterWidgetItem } from '../../../../vo/workingzone/gridster-widget-item';
import { PivotField } from '../../../../manager/baselines/pivot-field.obj';
import { AttachmentService } from '../../../../command/core/attachment/attachment.service';
import { Model } from '../../../../model/model';
import { IWidget } from '../../../../manager/widgets/i-widget';
import { AngularEditorComponent, AngularEditorConfig } from '@wfpena/angular-wysiwyg';


/** un champ complémentaire sur un FD (la structure de ce champ) */
export type FreeFormEditorData = { context?: GridsterWidgetItem };

/**
 * rendu des champs disponibles
 */
@Component({
  selector: 'freeform-renderer',
  templateUrl: './freeform.renderer.html',
})
export class FreeFormRenderer implements AfterViewInit, IWizItemRenderer<null, FreeFormEditorData> {
  @ViewChild('argContainer') argContainer: WizHgroup; // groupe principal
  
  @ViewChild('bt', { read: WizButton, static: false }) bt: WizButton; // fonction connect
  
  @ViewChild('dropGrp', { read: WizVgroup, static: false }) dropGrp: WizVgroup; // zone de drop de fichier

  @ViewChild('freeGrp', { read: WizHgroup, static: false }) freeGrp: WizHgroup; // rendu html libre
  @ViewChild('ae') ae: AngularEditorComponent; // bt save
    
  @ViewChild('freeImgGrp', { read: WizHgroup, static: false }) freeImgGrp: WizHgroup; // groupe image libre
    @ViewChild('freeImg') freeImg: WizImage; // image libre



  /* ##############################################################################
                  paramètres d'affichage
  ############################################################################## */

  //##### les activateurs de ngIf ######
  
  usebt = false; // fonction connect
  public cnx: ConnectVO = null; // fonction connect du bouton

  useDropGrp = false; // zone de drop de fichier

  usefreeGrp = false; // gestion rendu libre
  
  usefreeImgGrp = false; // image libre
  public imgSrcObs: Observable<string> = null;

  public formatedValue: any = null; // la valeur formatée pour le rendu dans les composants 
  

  /** configuration de l'editeur html */
  public editorConfig:AngularEditorConfig = {
    width: '100%',
    height: '100%',
    defaultFontSize: '2',
    editable: false,
    enableToolbar: false,
    showToolbar: false,
    spellcheck: false,
    //imageResizeSensitivity: 0
  }

  constructor(public elementRef: ElementRef) {}

  owner: null;
  private _data: FreeFormEditorData;
  @Input() public set data(src: FreeFormEditorData) {
    this._data = src;
  }
  public get data(): FreeFormEditorData {
    return this._data;
  }

  /** accesseur sur le field du WidgetItem, casté */
  public get field(): PivotField {
    const field: PivotField =  this._data?.context?.field as PivotField;
    return field;
  }

  /** accesseur sur le widget, casté */
  public get widget(): IWidget {
    return this.field?.widget;
  }

  public get model(): Model {
    return this.widget.model;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.feed(); // définition des selects et alimentation des champs de saisie
    }, 0);
  }


  /** 
   * mise à jour du renderer, sur init / sur modif du gridsterItem
   * prise en compte des FDs de FDs avec champ custom
   */
  public feed(): void {
    this.argContainer.wizDisabled = false;
    if (!this.data) {
      // console.log('maybe fake', this.data);
      return;
    }

    // la valeur de base
    let value: string = this.field.contextItem.lbl;
    this.formatedValue = value;
    switch(this.field?.contextItem?.fdvf) {
      case ViewData.FORM_HTML:
        this.usefreeGrp = true; 
        if(this.ae) {
          // console.log('feed value ArgR', value);
          
          this.ae.config = this.editorConfig;
          this.ae.placeholder='';
          this.ae.showPlaceholder = false;
          this.ae.writeValue(value);
        }
      break;
      case ViewData.FORM_ACTION:
        this.usebt = true; 
        this.cnx = this.model.connectMapExt?.get(this.model.domainCode)?.get(this.model.businessUnitCode)?.get(value);

      break;
      case ViewData.FORM_IMAGE:
        this.usefreeImgGrp = true;
        if(!!value) {
          this.imgSrcObs = AttachmentService.getInstance().downloadAttachmentForWidget(value);
        } else {
          this.imgSrcObs = of(null);
        }
        // console.log('image form', this.field.contextItem);
      break;
      case ViewData.FORM_DROP:
        this.useDropGrp = true;
      break;
    }
 
  }

  /** fonction liée au bouton d'action */
  public doArgAction(): void {    
    let cnxCode: string = this.field.contextItem.lbl;
    this.model.connectEngine.doArgActionExt(cnxCode, this.widget);
  }
  
  /**
   * neutralisation du dragOver, sinon le drop ne fonctionne pas...sous chrone
   */
  public dover(evt: DragEvent): void {
    evt.preventDefault();
    evt.stopImmediatePropagation();
    // console.log('dover', evt);
  }

  /**
   * Demande de chargement d'un fichier
   */
  public selectFile(): void {

    // console.log('selectFile', this.data);
    // TODO TODO passer context.slbl dans UploadParameter.noEncode

    let params: UploadParameter = {
      callback: null, // sera alimenté par le batchEngine
      accept: [WIZ_UPLOAD_ACCEPT_FORMAT.FILE_ANY],
      multiple: false, // false par défaut
      maxSize: 30, // 30 Mo
      elementRef: this.dropGrp.elementRef,
      targetPath: this.field.contextItem.subField,
      viewCallback: this.model.batchEngine.dropFileCallBack.bind(this.model.batchEngine),
      noEncode: this.data?.context?.source?.slbl || false // pas d'encodage
    }
    this.model.batchEngine.loadFile(params);
  }

  /**
   * Demande de chargement par drop
   */
  public dropFile(evt: DragEvent): void {
    evt.preventDefault();
    evt.stopImmediatePropagation();
    const files = evt.dataTransfer.files;
    let file: File = files[0];

    let params: UploadParameter = {
      callback: null, // sera alimenté par le batchEngine
      accept: [WIZ_UPLOAD_ACCEPT_FORMAT.FILE_ANY],
      multiple: false, // false par défaut
      maxSize: 30, // 30 Mo
      elementRef: this.dropGrp.elementRef,
      targetPath: this.field.contextItem.subField,
      files: [file],
      noEncode: this.data?.context?.source?.slbl || false // pas d'encodage
    }

    this.model.batchEngine.dropFile(params);
  }


}
